
import { TypedVue } from "@/store/types";
import { Component, Provide } from "vue-property-decorator";
import { ProjectReportData, Resource, V2Project } from "@/store/admin/adminTypes";
import { Action } from "vuex-class";
import { Actions } from "@/store/admin/adminActions";

import TeamEffectivenessReport from "@/components/project-reports/TeamEffectivenessReport.vue";
import TeamEffectivenessReportV2 from "@/components/project-reports/team-effectiveness-v2/TeamEffectivenessReportV2.vue";
import VueI18n from "vue-i18n";
import { getProjectReportLink } from "@/lib/projectReportLinks";
import { isTeamEffectivenessV2 } from "@/lib/projectReportLinks";

const namespace = "admin";

@Component({
  components: {
    TeamEffectivenessReport,
    TeamEffectivenessReportV2,
  },
})
export default class ProjectReport extends TypedVue {
  public report: ProjectReportData | null = null;
  public project: V2Project | null = null;
  public resource: Resource | null = null;

  @Action(Actions.GET_PROJECT_REPORT_DATA, { namespace })
  getProjectReportData!: ({ reportId, raw }: { reportId: string; raw?: boolean }) => Promise<ProjectReportData>;

  @Action(Actions.GET_PROJECT_BY_ID, { namespace })
  getProjectById!: (projectId: string) => Promise<V2Project>;

  @Action(Actions.GET_LITERALS, { namespace })
  getLiterals!: ({ module, version }: { module: string; version: string }) => Promise<Resource>;

  @Provide("$i18n") i18n = new VueI18n({
    locale: "en",
  });

  get loading() {
    return this.report === null || this.project === null || this.resource === null;
  }

  get reportId() {
    return this.$route.params.id;
  }

  get isV2(): boolean {
    return isTeamEffectivenessV2(this.report?.version);
  }

  async mounted() {
    this.report = await this.getProjectReportData({ reportId: this.reportId });

    // v1 and v2 share this view but live on different routes (v2 has no header anchor links).
    // Correct the route if a report was opened through the other version's URL.
    const expectedPath = getProjectReportLink({ identifier: this.reportId, type: "team-effectiveness", version: this.report.version });
    if (this.$route.path !== expectedPath) {
      await this.$router.replace({ path: expectedPath });
    }

    this.project = await this.getProjectById(this.report.projectId);

    // The literals doc is versioned alongside the report: v1 reports load the 1.x doc, v2 reports the 2.x doc.
    this.resource = await this.getLiterals({ module: `project-report:team-effectiveness`, version: this.report.version.split(".")[0] });
    this.i18n.setLocaleMessage("en", { ...this.$i18n.getLocaleMessage("en"), ...this.resource.dictionary });
  }
}
